import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';

/**
 * CarrierValidator - Validates if the carrier in URL matches the user's session carrier
 * Redirects to logout if carrier mismatch is detected
 * Excludes /logout and /SlpMember routes from validation
 */
const CarrierValidator = ({ children }) => {
    const location = useLocation();

    // Carrier code to URL name mapping (from .env)
    const CARRIER_CODE_TO_NAME = {
        'CG': 'cigna',
        'XC': 'christus',
        'X6': 'hcsc',
        'Q5': 'bcbsaz',
        'M1': 'mhc',
        'SY': 'sentara',
        'C4': 'molina',
        'C4CT': 'connecticare', // ConnectiCare special case
    };

    // Define allowed carrier names
    const ALLOWED_CARRIERS = Object.values(CARRIER_CODE_TO_NAME);

    useEffect(() => {
        // Extract carrier from URL
        const path = window.location.pathname;
        const carrierMatch = path.match(/^\/([^\/]+)/);
        
        if (!carrierMatch) {
            return; // No carrier in URL, allow access
        }

        const urlCarrier = carrierMatch[1].toLowerCase();

        // Exclude specific routes from validation
        const EXCLUDED_ROUTES = ['logout', 'slpmember', 'login', 'ssoauthcallback', 'ssoexternal'];
        if (EXCLUDED_ROUTES.includes(urlCarrier)) {
            return; // Allow access to excluded routes
        }
        
        // Check if current route is SSOMemberCheck (bypass validation during SSO login process)
        if (path.includes('/SSOMemberCheck') || path.includes('/ssomembercheck')) {
            console.log('CarrierValidator: Bypassing validation for SSOMemberCheck route');
            return; // Allow access during SSO login process
        }

        // Check if carrier is in the allowed list
        if (!ALLOWED_CARRIERS.includes(urlCarrier)) {
            console.warn(`Invalid carrier detected in URL: ${urlCarrier}`);
            window.location.href = '/logout';
            return;
        }

        // Get user's session carrier from cookies
        // For ConnectiCare, check connectiCareCarrier cookie first
        const connectiCareCarrier = Cookies.get('connectiCareCarrier');
        const owningCarrier = Cookies.get('owningCarrier');
        
        // If no owning carrier in session, allow access (login flow)
        if (!owningCarrier) {
            return;
        }

        // Use ConnectiCare carrier if available, otherwise use owning carrier
        const sessionCarrierCode = connectiCareCarrier || owningCarrier;
        
        // Map the session carrier code to URL name
        const sessionCarrierName = CARRIER_CODE_TO_NAME[sessionCarrierCode.toUpperCase()];

        // Check for carrier mismatch
        if (sessionCarrierName && sessionCarrierName !== urlCarrier) {
            console.warn(`Carrier mismatch detected! Session: ${sessionCarrierName}, URL: ${urlCarrier}`);
            window.location.href = '/logout';
            return;
        }

    }, [location.pathname]);

    return children;
};

export default CarrierValidator;
